import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/colors';
import { useRSVP } from '../../contexts/RSVPContext';
import { RSVPData } from '../../services/api';

const ErrorMessage = styled.div`
  color: ${colors.api.error};
  font-family: 'GremeryTwo', cursive;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
  font-style: italic;
  opacity: 0.9;
`;

const SuccessMessage = styled.div`
  color: #3c3;
  font-family: 'GremeryTwo', cursive;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
  font-style: italic;
  opacity: 0.9;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-family: 'GremeryTwo', cursive;
  color: ${colors.neutral.darkBrown};
`;

const GuestInput = styled.input`
  padding: 0.75rem 0 1.25rem 0;
  border: none;
  background: transparent;
  font-size: 1rem;
  font-family: 'GremeryTwo', cursive;
  transition: all 0.3s ease;
  color: #2c3e50;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #7f8c8d;
    opacity: 0.8;
    font-family: 'GremeryTwo', cursive;
  }
`;

const SelectedGuestInfo = styled.div`
  padding: 1rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid ${colors.primary.gold};
  margin-bottom: 1rem;
`;

const HelperText = styled.p`
  font-family: 'GremeryTwo', cursive;
  color: ${colors.neutral.darkBrown};
  font-size: 1rem;
  line-height: 1.5;
  margin: 0 0 1.5rem;
  text-align: center;
`;

const GuestName = styled.span`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.1rem;
  color: #2c3e50;
`;

const ClearButton = styled.button`
  background: transparent;
  color: #2c3e50;
  padding: 0.5rem 1rem;
  border-radius: 0;
  font-size: 0.9rem;
  font-family: 'GremeryOne', serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 1px;
  border: none;
  text-decoration: underline;
  
  &:hover {
    color: ${colors.primary.gold};
  }
`;

const SearchButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
`;

const FormContainer = styled.div`
  background: transparent;
  border-radius: 20px;
  padding: 3rem;
  border: none;
  backdrop-filter: none;
  box-shadow: none;
  max-width: 800px;
  margin: 0 auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PartyMemberRow = styled.div`
  margin-bottom: 0.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
`;

const Label = styled.label`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.1rem;
  color: #2c3e50;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const InputWrapper = styled.div`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: -15px;
    width: 100%;
    height: 15px;
    background-image: url('/images/underline.png');
    background-repeat: no-repeat;
    background-size: 100% 100%;
    background-position: center bottom;
    opacity: 0.7;
    pointer-events: none;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 0 1.25rem 0;
  border: none;
  background: transparent;
  font-size: 1rem;
  font-family: 'GremeryOne', cursive;
  line-height: 1.5;
  min-height: 80px;
  resize: none;
  transition: all 0.3s ease;
  color: #2c3e50;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #7f8c8d;
    opacity: 0.8;
    font-family: 'GremeryOne', cursive;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-family: 'GremeryTwo', cursive;
  font-size: 1rem;
  color: #2c3e50;
`;

const RadioInput = styled.input`
  accent-color: ${colors.primary.gold};
`;

const SubmitButton = styled.button`
  background: transparent;
  color: #2c3e50;
  padding: 1rem 2rem;
  border-radius: 0;
  font-size: 1.1rem;
  font-family: 'GremeryOne', serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 1px;
  margin-top: 1rem;

  &:hover {
    background: ${colors.primary.gold};
    color: white;
  }

  &:disabled {
    background: transparent;
    color: #ccc;
    border-color: #ccc;
    cursor: not-allowed;
  }
`;

const RSVPFormComponent: React.FC = () => {
  const {
    selectedGuest,
    currentRSVP,
    loadingGuestSearch,
    loadingRSVP,
    submittingRSVP,
    guestSearchError,
    rsvpError,
    searchGuest,
    submitRSVP,
    updateRSVP,
    clearSelection
  } = useRSVP();

  const [guestName, setGuestName] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [formData, setFormData] = useState({
    attendance: '',
    partyMembers: [] as { name: string; attending: boolean }[],
    message: ''
  });

  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Populate the editable reservation when a party is found.
  useEffect(() => {
    if (!selectedGuest) {
      setFormData({
        attendance: '',
        partyMembers: [],
        message: ''
      });
      return;
    }

    const savedMembers = currentRSVP?.partyMembers || [];
    const partyMembers = selectedGuest.searchableNames.map(name => {
      const savedMember = savedMembers.find(member => member.name === name);

      return {
        name,
        attending: savedMember?.attending ?? false
      };
    });

    setFormData({
      attendance: currentRSVP?.attendance || '',
      partyMembers,
      message: ''
    });
  }, [selectedGuest, currentRSVP]);

  const handleGuestSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim()) return;
    
    setHasSearched(true);
    setSubmitSuccess(false);
    await searchGuest(guestName.trim());
  };

  const handleGuestNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGuestName(e.target.value);
    setSubmitSuccess(false);
    if (hasSearched) {
      clearSelection();
      setHasSearched(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedGuest) {
      alert('Please select a guest first.');
      return;
    }

    setSubmitSuccess(false);

    const partyMembers = formData.partyMembers.map(member => ({
      ...member,
      attending: formData.attendance === 'yes' ? member.attending : false
    }));

    const rsvpData: RSVPData = {
      guestId: selectedGuest.id,
      attendance: formData.attendance as 'yes' | 'no',
      partyMembers,
      message: formData.message
    };

    let success = false;
    if (currentRSVP) {
      // Update existing RSVP
      success = await updateRSVP(currentRSVP.id, rsvpData);
    } else {
      // Submit new RSVP
      success = await submitRSVP(rsvpData);
    }

    if (success) {
      setSubmitSuccess(true);
    }
  };

  return (
    <FormContainer>
      <HelperText>
        Enter your first and last name to find your party, then update who can celebrate with us.
      </HelperText>

      {submitSuccess && (
        <SuccessMessage>
          Thank you! Your RSVP has been saved.
        </SuccessMessage>
      )}

      {(guestSearchError || rsvpError) && (
        <ErrorMessage>
          {guestSearchError || rsvpError}
        </ErrorMessage>
      )}

      {loadingGuestSearch ? (
        <LoadingMessage>Looking up your party...</LoadingMessage>
      ) : (
        <>
          <FormGroup>
            <Label htmlFor="guestName">your name</Label>
            {selectedGuest ? (
              <SelectedGuestInfo>
                <GuestName>
                  {selectedGuest.fullName}
                  {selectedGuest.searchableNames.length > 1 && (
                    <>
                      <br />
                      <small style={{ opacity: 0.7, fontSize: '0.9em' }}>
                        Party includes: {selectedGuest.searchableNames.join(', ')}
                      </small>
                    </>
                  )}
                </GuestName>
                <ClearButton
                  type="button"
                  onClick={() => {
                    clearSelection();
                    setGuestName('');
                    setHasSearched(false);
                    setSubmitSuccess(false);
                  }}
                >
                  Search again
                </ClearButton>
              </SelectedGuestInfo>
            ) : (
              <form onSubmit={handleGuestSearch}>
                <InputWrapper>
                  <GuestInput
                    type="text"
                    id="guestName"
                    value={guestName}
                    onChange={handleGuestNameChange}
                    placeholder="Enter your full name..."
                    disabled={loadingGuestSearch}
                    autoComplete="name"
                  />
                </InputWrapper>
                <SearchButtonWrapper>
                  <SubmitButton 
                    type="submit" 
                    disabled={loadingGuestSearch || !guestName.trim()}
                  >
                    {loadingGuestSearch ? 'Searching...' : 'Find my RSVP'}
                  </SubmitButton>
                </SearchButtonWrapper>
              </form>
            )}
          </FormGroup>

          {selectedGuest && (
            <div style={{ marginTop: '2rem' }}>
              {loadingRSVP ? (
                <LoadingMessage>Loading RSVP data...</LoadingMessage>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label>Will you be attending?</Label>
                    <RadioGroup>
                      <RadioOption>
                        <RadioInput
                          type="radio"
                          name="attendance"
                          value="yes"
                          checked={formData.attendance === 'yes'}
                          onChange={handleInputChange}
                          required
                        />
                        Yes, I'll be there!
                      </RadioOption>
                      <RadioOption>
                        <RadioInput
                          type="radio"
                          name="attendance"
                          value="no"
                          checked={formData.attendance === 'no'}
                          onChange={handleInputChange}
                          required
                        />
                        Sorry, can't make it
                      </RadioOption>
                    </RadioGroup>
                  </FormGroup>

                  {formData.attendance === 'yes' && (
                    <>
                      <FormGroup>
                        <Label>Who from your party will be attending?</Label>
                        {formData.partyMembers.map((member, index) => (
                          <PartyMemberRow key={member.name}>
                            <RadioOption>
                              <RadioInput
                                type="checkbox"
                                checked={member.attending}
                                onChange={(e) => {
                                  const updatedMembers = [...formData.partyMembers];
                                  updatedMembers[index].attending = e.target.checked;
                                  setFormData(prev => ({ ...prev, partyMembers: updatedMembers }));
                                }}
                              />
                              {member.name}
                            </RadioOption>
                          </PartyMemberRow>
                        ))}
                      </FormGroup>
                    </>
                  )}

                  <FormGroup>
                    <Label htmlFor="message">special message (optional)</Label>
                    <InputWrapper>
                      <TextArea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Any special message for the couple..."
                      />
                    </InputWrapper>
                  </FormGroup>

                  <SubmitButton type="submit" disabled={submittingRSVP}>
                    {submittingRSVP 
                      ? 'Submitting...' 
                      : currentRSVP 
                        ? 'Update RSVP' 
                        : 'Submit RSVP'}
                  </SubmitButton>
                </Form>
              )}
            </div>
          )}
        </>
      )}
    </FormContainer>
  );
};

export default RSVPFormComponent; 